import { Streemshot, StreemshotDetection } from '@streem/sdk-core';
import {
    dedupeDetections,
    ModelSerialList,
    useMediaBreakpoint,
    useObservable,
} from '@streem/sdk-react';
import {
    EditableNote,
    EditableNoteWithButton,
    MarkDownNotes,
    MarkDownNotesWithButton,
    PoweredByStreem,
    styled,
    Theme,
    ToastTypes,
} from '@streem/ui-react';
import { recordElementClicked } from '@streem/analytics';
import { FC, useCallback, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { streem } from 'streem-sdk-protobuf';
import debounce from 'lodash/debounce';
import { useDetailSession } from '../../../hooks/detail_session_hooks';
import { useAnonymousShareChecker } from '../../../hooks/use_anonymous_share_checker';
import { useGlobalStore } from '../../../hooks/use_global_context';
import appLogger from '../../../util/logging/app_logger';
import { MediaGallery } from '../../media_gallery/media_gallery';
import { CallDetailsLocation } from '../call_details_location';
import { SendNoteModal } from './send_note_modal';
import {
    CANCEL_SEND_NOTE_BUTTON_ID,
    CONFIRM_SEND_NOTE_BUTTON_ID,
    recordTooltipHover,
    SEND_NOTE_BUTTON_ID,
    timestampToDate,
} from './call_details_utils';
import { convertIsFinalizable } from './call_notes_utils';
import { FailedToSendNoteToastContent } from '../failed_to_send_note_toast_content';

interface CallDetailsPhotosNotesLegacyProps {
    readOnly?: boolean;
}

export const CallDetailsPhotosNotesModelLegacy = ({
    readOnly,
}: CallDetailsPhotosNotesLegacyProps): JSX.Element => {
    const log = appLogger.extend('edit call notes');
    const detailSession = useDetailSession();
    const { authStore, companySettingsStore, configStore, uiStore } = useGlobalStore();
    const isAnonymousSharePage = useAnonymousShareChecker(authStore);
    const { isMobile } = useMediaBreakpoint();
    const history = useHistory();

    const [roomNote] = useObservable(detailSession.note.roomNote);
    const sendableNoteYn = convertIsFinalizable(roomNote?.isFinalizable) !== 'CANNOT_BE_FINALIZED';
    const notReadyToFinalizeNote =
        convertIsFinalizable(roomNote?.isFinalizable) === 'NOT_READY_TO_FINALIZE';
    const finalizedAtDate = timestampToDate(roomNote?.finalizedAt);
    const [showMarkdown, setShowMarkdown] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const errorMessageRef = useRef<string | undefined>(undefined);
    const [showSendNoteModal, setShowSendNoteModal] = useState<boolean>(false);

    const [modelSerial = []] = useObservable(
        detailSession.detection.getDetections({
            types: [
                streem.api.Detection.Type.DETECTION_TYPE_MODEL,
                streem.api.Detection.Type.DETECTION_TYPE_SERIAL,
            ],
        }),
    );
    const [streemshots = []] = useObservable(detailSession.streemshots.responses);

    // must fetch the public (pre-auth) settings from configStore for this view
    // since it's displayed as an anonymous share link
    const gpsEnabled = configStore.gpsEnabled;
    const videoPreviewEnabled = configStore.videoTrackPreviewEnabled;
    const sendNoteEnabled = companySettingsStore.sendNoteEnabled;
    const editorNotesEnabled = companySettingsStore.editorNotesEnabled;

    const shouldShowLegacyMediaGallery =
        !editorNotesEnabled && (!isAnonymousSharePage || !isMobile);

    const handleEdit = useCallback(() => {
        setShowMarkdown(false);
    }, []);

    const onSave = async (noteText: string) => {
        setShowMarkdown(true);
        try {
            setErrorMessage(undefined);
            errorMessageRef.current = undefined;
            await detailSession.note.saveRoomNote(noteText);
        } catch (error) {
            errorMessageRef.current = 'Error attempting to edit call notes.';
            setErrorMessage('Error attempting to edit call notes.');
            log.error('Error attempting to edit call notes: ', error);
        }
    };

    const debounceShowSendNoteModal = useCallback(() => {
        const debouncer = debounce(() => {
            // can't rely on `errorMessage` but needed a way to prevent
            // the modal from displaying if we failed to update the wall
            // this should be removed in favor of a better solution when
            // notes area is refactored.
            if (errorMessageRef.current !== undefined) return;
            setShowSendNoteModal(true);
        }, 1_000);
        debouncer();
    }, []);

    const handlePrepareToSendNote = () => {
        recordElementClicked(SEND_NOTE_BUTTON_ID);
        // Debounce opening the modal to give the wall enough time to get updated
        // if the user clicks the button while still in edit mode.
        // This methods means we don't have to bring the 'draft' note up from
        // the text area component but instead just grab it from the wall once it's been updated.
        debounceShowSendNoteModal();
    };

    const handleSendNote = async () => {
        recordElementClicked(CONFIRM_SEND_NOTE_BUTTON_ID);
        try {
            setShowSendNoteModal(false);
            uiStore.removeToast(roomNote.text);
            await detailSession.note.finalizeRoomNote(roomNote.text);
        } catch {
            uiStore.addToastWithManualClose({
                id: roomNote.text,
                flavor: ToastTypes.ERROR,
                content: <FailedToSendNoteToastContent tryAgainHandler={() => handleSendNote()} />,
                manualClose: true,
            });
        }
    };

    const closeSendNoteModalOnCancel = () => {
        recordElementClicked(CANCEL_SEND_NOTE_BUTTON_ID);
        setShowSendNoteModal(false);
    };

    const handleModelSerialClick = (
        detection: StreemshotDetection,
        streemshotToDisplay?: Streemshot,
    ) => {
        const url = `${location.pathname}/artifact/${
            streemshotToDisplay?.id || detection.streemshotSid
        }`;
        history.push(url);
    };

    const dedupedModelSerial = useMemo(() => dedupeDetections(modelSerial), [modelSerial]);

    return (
        <>
            {isAnonymousSharePage && isMobile && (
                <div style={{ marginBottom: '40px' }}>
                    <MediaGallery videoPreviewEnabled={videoPreviewEnabled} readOnly={readOnly} />
                </div>
            )}
            {!editorNotesEnabled && (
                <>
                    <HorizontalBreak />
                    <TwoColumns isAnonymousSharePage={isAnonymousSharePage}>
                        {sendNoteEnabled && sendableNoteYn ? (
                            <CallNotesWithWebhook
                                showViewModeYn={showMarkdown}
                                text={roomNote?.text}
                                handleEdit={handleEdit}
                                isAnonymousSharePage={isAnonymousSharePage}
                                roomNoteCharLimit={companySettingsStore.roomNoteCharLimit}
                                onSave={onSave}
                                readOnly={readOnly}
                                errorMessage={errorMessage}
                                handlePrepareToSendNote={handlePrepareToSendNote}
                                finalizedAtDate={finalizedAtDate}
                                recordTooltipHover={recordTooltipHover}
                                notReadyToFinalizeNote={notReadyToFinalizeNote}
                            />
                        ) : (
                            <CallNotes
                                showViewModeYn={showMarkdown}
                                text={roomNote?.text}
                                handleEdit={handleEdit}
                                isAnonymousSharePage={isAnonymousSharePage}
                                roomNoteCharLimit={companySettingsStore.roomNoteCharLimit}
                                onSave={onSave}
                                readOnly={readOnly}
                                errorMessage={errorMessage}
                            />
                        )}

                        <div>
                            {gpsEnabled && <CallDetailsLocation />}
                            <ModelSerialList
                                modelSerialDetections={dedupedModelSerial}
                                streemshotBaseUrl={`${location.pathname}/artifact`}
                                streemshots={streemshots}
                                hasFootbar={false}
                                onClickHandler={handleModelSerialClick}
                            />
                        </div>
                    </TwoColumns>
                </>
            )}

            {shouldShowLegacyMediaGallery && (
                <MediaGallery videoPreviewEnabled={videoPreviewEnabled} readOnly={readOnly} />
            )}

            {isAnonymousSharePage && (
                <StreemFooterContainer>
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a href="https://www.streem.com/try-remote-video" target="_blank">
                        <PoweredByStreem />
                    </a>
                </StreemFooterContainer>
            )}
            <SendNoteModal
                isOpen={showSendNoteModal}
                sendNote={handleSendNote}
                closeModal={closeSendNoteModalOnCancel}
            />
        </>
    );
};

export interface CallNotesProps {
    showViewModeYn: boolean;
    text?: string;
    handleEdit: () => void;
    isAnonymousSharePage: boolean;
    roomNoteCharLimit: number;
    onSave: (noteText: string) => Promise<void>;
    readOnly?: boolean;
    errorMessage?: string;
    showTitle?: boolean;
    // optional props for Send Note Feature
    handlePrepareToSendNote?: () => void;
    finalizedAtDate?: Date | null;
    recordTooltipHover?: () => void;
    notReadyToFinalizeNote?: boolean;
}
export const CallNotes: FC<React.PropsWithChildren<CallNotesProps>> = ({
    showViewModeYn,
    text,
    handleEdit,
    isAnonymousSharePage,
    roomNoteCharLimit,
    onSave,
    readOnly,
    errorMessage,
    showTitle = true,
}) => {
    return showViewModeYn && text !== undefined ? (
        <MarkDownNotes
            text={text}
            onEdit={handleEdit}
            showTitle={showTitle}
            readOnly={isAnonymousSharePage}
        />
    ) : (
        <EditableNote
            description="Call Notes"
            placeholderText="Add call notes"
            characterLimit={roomNoteCharLimit}
            noteText={text}
            disableEditing={readOnly}
            onSave={onSave}
            errorMessage={errorMessage}
            hasFootBar={false}
            showChrome={false}
            isEditing={!showViewModeYn}
            showTitle={showTitle}
        />
    );
};

export const CallNotesWithWebhook: FC<React.PropsWithChildren<CallNotesProps>> = ({
    showViewModeYn,
    text,
    handleEdit,
    isAnonymousSharePage,
    roomNoteCharLimit,
    onSave,
    readOnly,
    errorMessage,
    showTitle = true,
    handlePrepareToSendNote,
    finalizedAtDate,
    recordTooltipHover,
    notReadyToFinalizeNote,
}) => {
    return showViewModeYn && text !== undefined ? (
        <MarkDownNotesWithButton
            text={text}
            onEdit={handleEdit}
            showTitle={showTitle}
            readOnly={isAnonymousSharePage}
            handlePrepareToSendNote={handlePrepareToSendNote}
            finalizedAtDate={finalizedAtDate}
            recordTooltipHover={recordTooltipHover}
            notReadyToFinalizeNote={notReadyToFinalizeNote}
        />
    ) : (
        <EditableNoteWithButton
            description="Call Notes"
            placeholderText="Add call notes"
            characterLimit={roomNoteCharLimit}
            noteText={text}
            disableEditing={readOnly}
            onSave={onSave}
            errorMessage={errorMessage}
            hasFootBar={false}
            showChrome={false}
            isEditing={!showViewModeYn}
            showTitle={showTitle}
            handlePrepareToSendNote={handlePrepareToSendNote}
            finalizedAtDate={finalizedAtDate}
            recordTooltipHover={recordTooltipHover}
            notReadyToFinalizeNote={notReadyToFinalizeNote}
        />
    );
};

const HorizontalBreak = styled.hr(({ theme }) => ({
    height: '1px',
    backgroundColor: theme.colors.grey10,
    border: 'none',
    margin: '24px 0',
}));

const TwoColumns = styled.div<{ isAnonymousSharePage: boolean }>(
    ({ isAnonymousSharePage, theme }) => ({
        display: 'grid',
        gridTemplateColumns: 'minMax(0, 1fr) minMax(0, 1fr)',
        gridTemplateRows: 'auto',
        columnGap: '32px',
        marginBottom: '24px',
        paddingBottom: '24px',
        borderBottom: isAnonymousSharePage ? 'none' : `solid 1px ${theme.colors.grey10}`,
        '@media (max-width: 500px)': {
            gridTemplateColumns: '1fr',
            gridRowGap: '32px',
        },
    }),
);

const StreemFooterContainer = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: Theme.colors.grey05,
    width: '100%',
    marginTop: '40px',
});
